import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  login as apiLogin,
  logout as apiLogout,
  isAuthenticated,
  clearTokens,
  getCurrentUser,
  getAccessToken,
  refreshAccessToken,
  SESSION_EXPIRED_EVENT
} from '../lib/api/api';

/** Roles of the firm model (server: src/services/firm_access.py). */
export type Role = 'super_admin' | 'firm_admin' | 'professional' | 'assistant';

const ROLES: readonly Role[] = ['super_admin', 'firm_admin', 'professional', 'assistant'];

export interface FirmInfo {
  id: string;
  name: string;
  code: string;
}

export interface AuthUser {
  email: string;
  fullName: string | null;
  role: Role;
  /** The user's firm; null for the super admin, who belongs to none. */
  firm: FirmInfo | null;
  preferredCanton: string;
}

/** The session as the client keeps it: the user plus the server's capability flags. */
export interface MappedProfile {
  user: AuthUser;
  /** Manages the firm's members, letterhead and policies (server-computed). */
  canManageFirm: boolean;
  /** May change the shared legal corpus: super admin only (server-computed). */
  canManageCorpus: boolean;
}

/**
 * The single mapping of the server profile (`/auth/me`, the `user` of the
 * login response) to the client session. An unknown role falls back to the
 * least privileged one.
 */
export function mapUser(data: any): MappedProfile {
  const role: Role = ROLES.includes(data?.role) ? data.role : 'assistant';
  const firm = data?.firm ? { id: data.firm.id, name: data.firm.name, code: data.firm.code } : null;
  return {
    user: {
      email: data?.email ?? '',
      fullName: data?.full_name ?? null,
      role,
      firm,
      preferredCanton: data?.preferred_canton || 'TI',
    },
    canManageFirm: !!data?.can_manage_firm,
    canManageCorpus: !!data?.can_manage_corpus,
  };
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when the user was logged out because the session could not be renewed. */
  sessionExpired: boolean;
  isSuperAdmin: boolean;
  isFirmAdmin: boolean;
  canManageFirm: boolean;
  canManageCorpus: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  sessionExpired: false,
  isSuperAdmin: false,
  isFirmAdmin: false,
  canManageFirm: false,
  canManageCorpus: false,
  login: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<MappedProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const isAuthenticatedRef = useRef(false);
  isAuthenticatedRef.current = isUserAuthenticated;

  // The API layer dispatches 'session-expired' when a token refresh fails for
  // good (refresh token expired/revoked): drop back to the login screen.
  useEffect(() => {
    const handleSessionExpired = () => {
      if (isAuthenticatedRef.current) setSessionExpired(true);
      setProfile(null);
      setIsUserAuthenticated(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  // Restore the session on mount (page reload: refresh token in sessionStorage)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!isAuthenticated()) {
          setIsUserAuthenticated(false);
          return;
        }

        // A refresh token but no access token (page reload): refresh first
        if (!getAccessToken()) {
          try {
            await refreshAccessToken();
          } catch (error) {
            console.error('Token refresh failed on page load:', error);
            clearTokens();
            setIsUserAuthenticated(false);
            return;
          }
        }

        setIsUserAuthenticated(true);

        try {
          const response = await getCurrentUser();
          setProfile(mapUser(response.data));
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          clearTokens();
          setIsUserAuthenticated(false);
          setProfile(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearTokens();
        setIsUserAuthenticated(false);
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login. Deliberately does NOT toggle the global isLoading: App swaps the
  // whole tree for a spinner while it is true, which would remount the login
  // form and wipe its error message and typed email. The form keeps its own
  // "submitting" state instead.
  const login = async (email: string, password: string) => {
    try {
      const response = await apiLogin(email, password);
      // The login response carries the profile; /auth/me if it ever does not
      const data = response.data?.user ?? (await getCurrentUser()).data;
      setProfile(mapUser(data));
      setSessionExpired(false);
      setIsUserAuthenticated(true);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setProfile(null);
      setIsUserAuthenticated(false);
      setSessionExpired(false);
      setIsLoading(false);
    }
  };

  // Re-read /auth/me. Call after any change of the profile on the server
  // (e.g. PATCH /auth/me/preferences) so the session stays in sync without a
  // page reload.
  const refreshUser = async (): Promise<void> => {
    try {
      const response = await getCurrentUser();
      setProfile(mapUser(response.data));
    } catch (error) {
      console.error('refreshUser failed:', error);
    }
  };

  const user = profile?.user ?? null;
  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: isUserAuthenticated,
    sessionExpired,
    isSuperAdmin: user?.role === 'super_admin',
    isFirmAdmin: user?.role === 'firm_admin',
    canManageFirm: profile?.canManageFirm ?? false,
    canManageCorpus: profile?.canManageCorpus ?? false,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
